import { SITE_DESCRIPTION, SITE_TITLE, SITE_URL } from "@/config/site";
import type { Project } from "@/content/types";

export interface PageMetadata {
  title: string;
  description: string;
  canonical: string;
  type?: "website" | "article";
  image?: string;
}

export const homeMetadata: PageMetadata = {
  title: SITE_TITLE,
  description: SITE_DESCRIPTION,
  canonical: `${SITE_URL}/`,
  type: "website",
};

export const notFoundMetadata: PageMetadata = {
  title: `Page not found | ${SITE_TITLE}`,
  description: "The requested page could not be found.",
  canonical: `${SITE_URL}/404`,
};

export function getProjectMetadata(project: Project): PageMetadata {
  return {
    title: project.seo.title,
    description: project.seo.description,
    canonical: `${SITE_URL}/projects/${project.slug}`,
    type: "article",
    image: project.seo.image ? new URL(project.seo.image, SITE_URL).toString() : undefined,
  };
}

function setMeta(selector: string, value: string): void {
  const element = document.head.querySelector<HTMLMetaElement>(selector);
  element?.setAttribute("content", value);
}

export function applyMetadata(metadata: PageMetadata): void {
  document.title = metadata.title;
  setMeta('[data-meta="description"]', metadata.description);
  setMeta('[data-meta="og:title"]', metadata.title);
  setMeta('[data-meta="og:description"]', metadata.description);
  setMeta('[data-meta="og:url"]', metadata.canonical);
  setMeta('[data-meta="og:type"]', metadata.type ?? "website");
  setMeta('[data-meta="twitter:title"]', metadata.title);
  setMeta('[data-meta="twitter:description"]', metadata.description);

  const canonical = document.head.querySelector<HTMLLinkElement>('[data-meta="canonical"]');
  canonical?.setAttribute("href", metadata.canonical);

  const existingImage = document.head.querySelector<HTMLMetaElement>('[data-meta="og:image"]');
  if (metadata.image) {
    const image = existingImage ?? document.createElement("meta");
    image.setAttribute("property", "og:image");
    image.setAttribute("data-meta", "og:image");
    image.setAttribute("content", metadata.image);
    if (!existingImage) document.head.append(image);
  } else {
    existingImage?.remove();
  }
}
